import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Upload,
  Button,
  Avatar,
  message,
  Tabs,
  Row,
  Col,
  Select,
  Spin,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  CameraOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  BellOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import apiClient from "../api/apiClient";
import SecureLogger from "../utils/secureLogger.js";
import API_CONFIG from "../config/api";
import {
  getTimezoneOptions,
  normalizeTimezone,
} from "../utils/timezoneOptions";
import WebPushSettings from "./WebPushSettings";
import AccountRecoverySettings from "./AccountRecoverySettings";
import DailyRecapBriefingSettings from "./DailyRecapBriefingSettings";
import WhatsAppRecapScheduleSettings from "./WhatsAppRecapScheduleSettings";
import { requestFeedbackForm } from "../gestion-feedback/feedbackWidgetSession";
import "./UserProfileModal.css";

// Utilise la configuration API centralisée pour les URLs d'images
const getImageUrl = (imagePath) => {
  if (!imagePath) return undefined;
  if (imagePath.startsWith("http")) return imagePath;
  const path = imagePath.startsWith("/")
    ? imagePath
    : `/uploads/profiles/${imagePath}`;
  return `${API_CONFIG.BASE_URL}${path}`;
};

const beforeProfileImageUpload = (file) => {
  const isSupportedImage =
    ["image/jpeg", "image/png"].includes(file.type) ||
    /\.(jpe?g|png)$/i.test(file.name);

  if (!isSupportedImage) {
    message.error("Veuillez sélectionner une image au format PNG ou JPG.");
    return Upload.LIST_IGNORE;
  }

  const isWithinSizeLimit = file.size / 1024 / 1024 <= 5;
  if (!isWithinSizeLimit) {
    message.error("L’image ne doit pas dépasser 5 Mo.");
    return Upload.LIST_IGNORE;
  }

  return false;
};

const ProfileImageUploader = ({ fileList = [], onChange, disabled }) => {
  const [previewUrl, setPreviewUrl] = useState();
  const currentFile = fileList[0];

  useEffect(() => {
    let objectUrl;

    if (currentFile?.originFileObj) {
      objectUrl = URL.createObjectURL(currentFile.originFileObj);
      setPreviewUrl(objectUrl);
    } else {
      setPreviewUrl(currentFile?.thumbUrl || currentFile?.url);
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [currentFile]);

  return (
    <div className="user-profile-modal__photo-card">
      <Avatar
        className="user-profile-modal__photo-avatar"
        size={88}
        src={previewUrl}
        icon={<UserOutlined />}
      />

      <div className="user-profile-modal__photo-details">
        <span
          className="user-profile-modal__photo-name"
          title={currentFile?.name || "Aucune image sélectionnée"}
        >
          {currentFile?.name || "Aucune image sélectionnée"}
        </span>

        <Upload
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          beforeUpload={beforeProfileImageUpload}
          disabled={disabled}
          fileList={fileList}
          maxCount={1}
          onChange={onChange}
          showUploadList={false}
        >
          <Button
            className="user-profile-modal__photo-button"
            disabled={disabled}
            icon={<CameraOutlined />}
          >
            {currentFile ? "Changer la photo" : "Ajouter une photo"}
          </Button>
        </Upload>

        <span className="user-profile-modal__photo-help">
          PNG ou JPG · 5 Mo maximum
        </span>
      </div>
    </div>
  );
};

const UserProfileModal = ({ visible, onCancel }) => {
  const { user, refreshUserStatus, syncUserProfile } = useAuth();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [modalLoading, setModalLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const pendingFeedbackUser = useRef(null);
  const passwordTabVisited = useRef(false);
  const timezoneOptions = useMemo(
    () => getTimezoneOptions(user?.timezone),
    [user?.timezone],
  );

  useEffect(() => {
    if (visible) {
      setActiveTab("profile");
    } else if (passwordTabVisited.current) {
      passwordForm.resetFields();
      passwordTabVisited.current = false;
    }
  }, [visible, passwordForm]);

  useEffect(() => {
    if (visible && user) {
      // Charger les données utilisateur actuelles dans le formulaire
      const profileImageFileList = user.profile_image
        ? [
            {
              uid: "-1",
              name: user.profile_image?.split("/")?.pop() || "image.png",
              status: "done",
              url: getImageUrl(user.profile_image),
              thumbUrl: getImageUrl(user.profile_image),
            },
          ]
        : [];

      profileForm.setFieldsValue({
        name: user.name,
        email: user.email,
        phone: user.phone,
        address: user.address,
        timezone: normalizeTimezone(user.timezone) || undefined,
        profile_image: profileImageFileList,
      });
    }
  }, [visible, user, profileForm]);

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const handleUpdateProfile = async () => {
    try {
      const values = await profileForm.validateFields();
      setModalLoading(true);

      const formData = new FormData();

      // Populate FormData - même logique que GestionStaff.jsx
      Object.keys(values).forEach((key) => {
        const value = values[key];

        if (key === "profile_image") {
          // Handle file upload
          if (value && value.length > 0 && value[0].originFileObj) {
            formData.append(key, value[0].originFileObj);
          }
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      await apiClient.put(`/api/users/${user.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      syncUserProfile(values);
      const profileRefreshed = await refreshUserStatus();
      if (!profileRefreshed) {
        SecureLogger.warn(
          "Profile saved but immediate user refresh was unavailable",
        );
      }

      message.success("Profil mis à jour avec succès !");
      onCancel();
    } catch (error) {
      SecureLogger.error("Failed to update user profile");
      const errorMsg =
        error.response?.data?.error ||
        "Une erreur s'est produite lors de la mise à jour.";
      message.error(errorMsg);
    } finally {
      setModalLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      setModalLoading(true);

      // Utiliser le nouvel endpoint spécifique pour le changement de mot de passe
      await apiClient.put(`/api/users/${user.id}/change-password`, {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      message.success("Mot de passe modifié avec succès !");
      passwordForm.resetFields();
    } catch (error) {
      SecureLogger.error("Failed to change password");
      const errorMsg =
        error.response?.data?.error ||
        "Erreur lors du changement de mot de passe.";
      message.error(errorMsg);
    } finally {
      setModalLoading(false);
    }
  };

  const modalTitle = (
    <div className="user-profile-modal__header">
      <Avatar
        className="user-profile-modal__header-avatar"
        size={56}
        src={getImageUrl(user?.profile_image)}
        icon={<UserOutlined />}
      />
      <div className="user-profile-modal__header-copy">
        <div className="user-profile-modal__heading">Mon profil</div>
        <div className="user-profile-modal__subtitle">
          Gérez vos informations personnelles, votre sécurité et vos notifications
        </div>
      </div>
    </div>
  );

  const profileTab = (
    <Form
      className="user-profile-modal__form"
      form={profileForm}
      layout="vertical"
    >
      <Row gutter={[32, 0]}>
        <Col xs={24} md={12}>
          <Form.Item
            className="user-profile-modal__photo-form-item"
            name="profile_image"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <ProfileImageUploader disabled={modalLoading} />
          </Form.Item>

          <Form.Item
            name="name"
            label="Nom complet"
            rules={[
              { required: true, message: "Veuillez entrer le nom !" },
            ]}
          >
            <Input placeholder="Entrer le nom complet" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Adresse e-mail"
            rules={[
              { required: true, message: "Veuillez entrer l’e-mail !" },
              { type: "email", message: "Format d’e-mail invalide !" },
            ]}
          >
            <Input
              autoComplete="email"
              placeholder="Entrer l’adresse e-mail"
              type="email"
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item name="phone" label="Numéro de téléphone">
            <Input
              autoComplete="tel"
              inputMode="tel"
              placeholder="Entrer le numéro de téléphone"
            />
          </Form.Item>

          <Form.Item name="address" label="Adresse">
            <Input.TextArea
              autoComplete="street-address"
              placeholder="Entrer l’adresse"
              rows={4}
            />
          </Form.Item>

          <Form.Item
            name="timezone"
            label="Fuseau horaire"
            rules={[
              {
                required: true,
                message: "Veuillez sélectionner un fuseau horaire.",
              },
            ]}
          >
            <Select
              optionFilterProp="label"
              options={timezoneOptions}
              placeholder="Sélectionner un fuseau horaire"
              showSearch
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );

  const passwordTab = (
    <div className="user-profile-modal__security-panel">
      <div className="user-profile-modal__security-intro">
        <span className="user-profile-modal__security-icon">
          <LockOutlined />
        </span>
        <div>
          <h3>Modifier votre mot de passe</h3>
          <p>
            Choisissez un mot de passe unique que vous n’utilisez sur aucun
            autre service.
          </p>
        </div>
      </div>

      <Form
        className="user-profile-modal__form user-profile-modal__password-form"
        form={passwordForm}
        layout="vertical"
      >
        <Form.Item
          name="currentPassword"
          label="Mot de passe actuel"
          rules={[
            {
              required: true,
              message: "Veuillez entrer votre mot de passe actuel !",
            },
          ]}
        >
          <Input.Password
            autoComplete="current-password"
            placeholder="Mot de passe actuel"
            iconRender={(isVisible) =>
              isVisible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="Nouveau mot de passe"
          extra="Au moins 6 caractères."
          rules={[
            {
              required: true,
              message: "Veuillez entrer un nouveau mot de passe !",
            },
            {
              min: 6,
              message: "Le mot de passe doit contenir au moins 6 caractères",
            },
          ]}
          hasFeedback
        >
          <Input.Password
            autoComplete="new-password"
            placeholder="Nouveau mot de passe"
            iconRender={(isVisible) =>
              isVisible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirmer le nouveau mot de passe"
          dependencies={["newPassword"]}
          hasFeedback
          rules={[
            {
              required: true,
              message: "Veuillez confirmer votre nouveau mot de passe !",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Les deux mots de passe ne correspondent pas !")
                );
              },
            }),
          ]}
        >
          <Input.Password
            autoComplete="new-password"
            placeholder="Confirmer le nouveau mot de passe"
            iconRender={(isVisible) =>
              isVisible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>
      </Form>
    </div>
  );

  const notificationsTab = (
    <div className="user-profile-modal__notifications-stack">
      <DailyRecapBriefingSettings />
      <WhatsAppRecapScheduleSettings active={visible && activeTab === "notifications"} />
      <WebPushSettings />
    </div>
  );

  const modalFooter =
    activeTab === "notifications" || activeTab === "recovery" ? (
      <div className="user-profile-modal__footer">
        <Button onClick={onCancel}>Fermer</Button>
      </div>
    ) : (
      <div className="user-profile-modal__footer">
        <Button disabled={modalLoading} onClick={onCancel}>
          Annuler
        </Button>
        <Button
          type="primary"
          loading={modalLoading}
          onClick={
            activeTab === "profile"
              ? handleUpdateProfile
              : handleChangePassword
          }
        >
          {activeTab === "profile"
            ? "Enregistrer les modifications"
            : "Changer le mot de passe"}
        </Button>
      </div>
    );

  return (
    <Modal
      centered
      className="user-profile-modal"
      title={modalTitle}
      open={visible}
      onCancel={onCancel}
      afterClose={() => {
        const requestedUserId = pendingFeedbackUser.current;
        pendingFeedbackUser.current = null;
        if (requestedUserId && Number(requestedUserId) === Number(user?.id)) requestFeedbackForm(requestedUserId);
      }}
      footer={modalFooter}
      width={920}
      destroyOnClose
    >
      <Spin spinning={modalLoading} tip="Chargement...">
        {user && !user.subscription_portal_only && <div className="user-profile-modal__feedback-actions">
          <Button className="user-profile-modal__feedback" icon={<CommentOutlined aria-hidden="true" />}
            aria-label="Donner mon avis" disabled={modalLoading}
            onClick={() => { pendingFeedbackUser.current = user.id; onCancel(); }}>
            Donner mon avis
          </Button>
        </div>}
        <Tabs
          activeKey={activeTab}
          onChange={(key) => { if (key === "password") passwordTabVisited.current = true; setActiveTab(key); }}
          items={[
            {
              key: "profile",
              label: (
                <span
                  className="user-profile-modal__tab-label"
                  aria-label="Informations personnelles"
                >
                  <UserOutlined />
                  <span>Informations personnelles</span>
                </span>
              ),
              children: profileTab,
            },
            {
              key: "password",
              label: (
                <span
                  className="user-profile-modal__tab-label"
                  aria-label="Mot de passe et sécurité"
                >
                  <LockOutlined />
                  <span>Mot de passe et sécurité</span>
                </span>
              ),
              children: passwordTab,
            },
            {
              key: "recovery",
              label: "Récupération du compte",
              children: <AccountRecoverySettings active={visible && activeTab === "recovery"} />,
            },
            ...(!user?.subscription_portal_only
              ? [
                  {
                    key: "notifications",
                    label: (
                      <span
                        className="user-profile-modal__tab-label"
                        aria-label="Notifications"
                      >
                        <BellOutlined />
                        <span>Notifications</span>
                      </span>
                    ),
                    children: notificationsTab,
                  },
                ]
              : []),
          ]}
        />
      </Spin>
    </Modal>
  );
};

export default UserProfileModal;
